import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { setLangueFromLicence } from "./i18n/i18n";
import { LOGO_CIVISOFT, LOGO_GEO5 } from "./constants";
import { chargerProfil } from "./utils/profil";
import PagePublique from "./components/PagePublique";
import AccesExpire from "./components/AccesExpire";
import AdminPanel  from "./components/AdminPanel";
import PageAccueil    from "./components/PageAccueil";
import PageProjet     from "./components/PageProjet";
import { PageSolution } from "./components/PageSolution";
import PageRapport      from "./components/PageRapport";
import TabAgentIA     from "./components/TabAgentIA";
import TabCoPilote    from "./components/TabCoPilote";
import TabComparaison from "./components/TabComparaison";
import PopupProfil    from "./components/PopupProfil";
import { HelpModal }  from "./components/shared/HelpSystem";
import FormulaireClient from "./components/FormulaireClient";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

export default function App() {
  const { t } = useTranslation();

  // ── Auth ────────────────────────────────────────────
  const [authState, setAuthState]     = useState("checking");
  const [currentUser, setCurrentUser] = useState(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  // ── App ─────────────────────────────────────────────
  const [onglet, setOnglet]                       = useState("presentation");
  const [etapeProjet, setEtapeProjet]             = useState(0);
  const [donneesProjet, setDonneesProjet]         = useState(null);
  const [profilUtilisateur, setProfilUtilisateur] = useState(() => chargerProfil());
  const [showPopupProfil, setShowPopupProfil]     = useState(() => !chargerProfil());
  const [showHelp, setShowHelp]                   = useState(false);
  const [showProjets, setShowProjets]             = useState(false);
  const [showNouveauProjet, setShowNouveauProjet] = useState(false);
  const [projetModifie, setProjetModifie]         = useState(false);
  const [verifModal, setVerifModal]               = useState(null);
  const [diagnosticBackend, setDiagnosticBackend] = useState(null);
  const [modeConsultationProjet, setModeConsultationProjet] = useState(false);

  // ── Projets sauvegardés (localStorage — clé par utilisateur) ──────────────
  const getProjetsKey = (user) => `geo5_projets_${(user || currentUser)?.id || "guest"}`;
  const getProjets    = (user) => JSON.parse(localStorage.getItem(getProjetsKey(user)) || "[]");
  const [projetsListe, setProjetsListe] = useState([]);

  // Recharger les projets dès que l'utilisateur est connu
  useEffect(() => {
    if (currentUser) setProjetsListe(getProjets(currentUser));
    else setProjetsListe([]);
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const chargerProjet = (projet) => {
    const dp = projet.donneesProjet || {};
    const id = projet.id || dp._projetId || Date.now();

    const projetComplet =
      dp.typeOuvrage &&
      Array.isArray(dp.couches) &&
      dp.couches.length > 0;

    const solutionsPresentes =
      Array.isArray(dp.solutions) &&
      dp.solutions.length > 0;

    setDonneesProjet({ ...dp, _projetId: id });
    setDiagnosticBackend(null);
    setEtapeProjet(0);
    setProjetModifie(false);
    setShowProjets(false);

    if (!projetComplet) {
      setModeConsultationProjet(false);
      setOnglet("projet");
    } else if (solutionsPresentes) {
      setModeConsultationProjet(true);
      setOnglet("solution");
    } else {
      setModeConsultationProjet(false);
      setOnglet("solution");
    }
  };

  const handleNouveauProjet = (clientInfo) => {
    const newDonnees = { clientInfo, _projetId: Date.now() };
    const projets = getProjets();
    const entree = {
      id: Date.now().toString(),
      nom: clientInfo.nomEntreprise + (clientInfo.codeRef ? ` — ${clientInfo.codeRef}` : ""),
      date: new Date().toISOString(),
      donneesProjet: newDonnees,
    };
    projets.push(entree);
    localStorage.setItem(getProjetsKey(), JSON.stringify(projets));
    setProjetsListe(getProjets());
    setDonneesProjet(newDonnees);
    setEtapeProjet(0);
    setProjetModifie(false);
    setModeConsultationProjet(false);
    setShowNouveauProjet(false);
    setOnglet("projet");
  };

  const supprimerProjet = (id) => {
    const maj = projetsListe.filter(p => p.id !== id);
    localStorage.setItem(getProjetsKey(), JSON.stringify(maj));
    setProjetsListe(maj);
  };

  // ── Vérification token au démarrage ─────────────────
  useEffect(() => {
    if (process.env.NODE_ENV === "development" || process.env.REACT_APP_DEV_MODE === "true") {
      setCurrentUser({ id:1, email:"admin@civisoft.fr", nom:"Admin", is_admin:true, has_copilote:true, copilote_mode:"complet", licence:"fr" });
      setAuthState("app");
      return;
    }
    // Magic link : ?token=xxx dans l'URL
    const urlParams = new URLSearchParams(window.location.search);
    const magicToken = urlParams.get("token");
    if (magicToken) {
      localStorage.setItem("geo5_token", magicToken);
      window.history.replaceState({}, "", window.location.pathname); // retire le token de l'URL
    }
    const token = magicToken || localStorage.getItem("geo5_token");
    if (!token) { setAuthState("accueil"); return; }
    fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (r.status === 403) { setAuthState("expire"); return null; }
        if (!r.ok)            { setAuthState("accueil");  return null; }
        return r.json();
      })
      .then(data => { if (data) { setCurrentUser(data); setLangueFromLicence(data?.licence); setShowPopupProfil(false); setAuthState(data.is_admin ? "admin" : "app"); } })
      .catch(() => setAuthState("accueil"));
  }, []);

  const handleLogin = (user, flag) => {
    if (flag === "EXPIRE") { setAuthState("expire"); return; }
    setCurrentUser(user);
    setLangueFromLicence(user?.licence);
    setAuthState("app");
  };

  const [showDeconnexionConfirm, setShowDeconnexionConfirm] = useState(false);
  const [copyrightAccepted, setCopyrightAccepted] = useState(false);
  const [copyrightChecked, setCopyrightChecked] = useState(false);

  // Vérifier si cet utilisateur a déjà approuvé sur cet appareil
  useEffect(() => {
    if (currentUser) {
      const key = `geo5_copyright_${currentUser.id || currentUser.email}`;
      setCopyrightAccepted(localStorage.getItem(key) === "1");
    }
  }, [currentUser]);

  const demanderDeconnexion = () => {
    // Si un projet est ouvert et pas encore analysé, demander confirmation
    if (donneesProjet) {
      setShowDeconnexionConfirm(true);
    } else {
      handleDeconnexion();
    }
  };

  const handleDeconnexion = () => {
    localStorage.removeItem("geo5_token");
    localStorage.removeItem("geo5_user");
    setCurrentUser(null);
    setAuthState("accueil");
    setShowDeconnexionConfirm(false);
  };

  const onProjetClone = (nouvelleEntree) => {
    setProjetsListe(getProjets());
    setDonneesProjet(nouvelleEntree.donneesProjet);
    setProjetModifie(false);
    setModeConsultationProjet(false);
    setEtapeProjet(0);
  };

  const sauvegarderEtQuitter = () => {
    if (donneesProjet) {
      const storageKey = getProjetsKey();
      const projets = JSON.parse(localStorage.getItem(storageKey) || "[]");
      const clientInfo = donneesProjet.clientInfo;
      const nom = clientInfo?.nomEntreprise
        ? `${clientInfo.nomEntreprise}${clientInfo.codeRef ? ` — ${clientInfo.codeRef}` : ""}`
        : `Projet ${new Date().toLocaleDateString("fr-FR")}`;
      // Mise à jour si même codeRef, sinon ajout
      const idx = projets.findIndex(p => p.donneesProjet?.clientInfo?.codeRef && p.donneesProjet.clientInfo.codeRef === clientInfo?.codeRef);
      const entree = { id: idx >= 0 ? projets[idx].id : Date.now().toString(), nom, date: new Date().toISOString(), donneesProjet };
      if (idx >= 0) projets[idx] = entree; else projets.push(entree);
      localStorage.setItem(storageKey, JSON.stringify(projets));
    }
    handleDeconnexion();
  };

  const handleAdmin = (user) => {
    setCurrentUser(user);
    setShowPopupProfil(false);
    setAuthState("admin");
  };

  // Navigation vers une étape du projet
  const allerProjet = (etape) => {
    setModeConsultationProjet(false);
    setEtapeProjet(etape);
    setOnglet("projet");
  };

  // ── KB VERIF-01→06 : vérification cohérence avant analyse ──────────────
  const formatSolKB = (sol = "") => sol.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());

  const verifierCoherenceDonnees = (dp) => {
    const erreurs = [];
    const avertissements = [];

    // VERIF-01 — Hauteur obligatoire
    if (!dp.hauteur || parseFloat(dp.hauteur) <= 0)
      erreurs.push({ id:"VERIF-01", champ:"Hauteur", message:"Hauteur / Profondeur obligatoire et > 0" });

    // VERIF-02 — Type ouvrage obligatoire
    if (!dp.typeOuvrage)
      erreurs.push({ id:"VERIF-02", champ:"Type d'ouvrage", message:"Sélectionner un type d'ouvrage" });

    // VERIF-03 — Zone sismique obligatoire
    if (!dp.zoneSismique)
      erreurs.push({ id:"VERIF-03", champ:"Zone sismique", message:"Zone sismique EC8 obligatoire" });

    // VERIF-04 — Sol obligatoire
    if (!(dp.couches?.length > 0))
      erreurs.push({ id:"VERIF-04", champ:"Données terrain", message:"Au moins une couche géologique requise" });

    // VERIF-05 — Rdt cohérence Ménard
    const rdt     = parseFloat(dp.rdt);
    const typeSol = dp.couches?.[0]?.nature || dp.couches?.[0]?.typeSol;
    if (rdt && typeSol) {
      const rdtMenard = {
        argile_molle:[50,120], argile_ferme:[120,300],
        limon:[80,200], sable_lache:[100,250],
        sable_dense:[300,800], gravier:[400,1000],
      };
      const plage = rdtMenard[typeSol];
      if (plage && (rdt < plage[0] || rdt > plage[1]))
        avertissements.push({
          id:"VERIF-05", champ:"Résistance Rdt",
          message:`Rdt = ${rdt} kPa inhabituel pour ${formatSolKB(typeSol)} (plage typique : ${plage[0]}–${plage[1]} kPa). Justifier par essai PMT/CPT.`,
        });
    }

    // VERIF-06 — Nappe présente sans essais labo
    if (dp.nappe && !["nulle","absente","basse"].includes(dp.nappe) && !(dp.essais || []).includes("labo"))
      avertissements.push({
        id:"VERIF-06", champ:"Nappe phréatique",
        message:"Nappe influente sans essais labo — perméabilité non mesurée (ISO 17892-6)",
      });

    return { erreurs, avertissements };
  };

  const lancerAnalyse = (data) => {
    const { erreurs, avertissements } = verifierCoherenceDonnees(data);
    if (erreurs.length > 0 || avertissements.length > 0) {
      setVerifModal({ erreurs, avertissements, pendingData: data });
    } else {
      setModeConsultationProjet(false);
      setDonneesProjet(data);
      setDiagnosticBackend(null);
      setOnglet("solution");
    }
  };

  // Détermine si l'analyse a été lancée (solutions disponibles)
  const analyseComplete   = !!(donneesProjet?.solutions?.length > 0);
  const projetOuvert      = !!donneesProjet;
  const analyseNavActive  = projetOuvert && !!donneesProjet?.typeOuvrage;

  // Items du menu vert
  const NAV_ITEMS = [
    { label: t("nav.projets"),      action: () => setOnglet("demarrage"),   actif: onglet === "demarrage",                       bloque: false },
    { label: t("nav.donnees_projet"), action: () => allerProjet(0),         actif: onglet === "projet" && etapeProjet === 0,      bloque: !projetOuvert },
    { label: t("nav.donnees_terrain"), action: () => allerProjet(1),        actif: onglet === "projet" && etapeProjet === 1,      bloque: !projetOuvert },
    { label: t("nav.contraintes"),  action: () => allerProjet(2),           actif: onglet === "projet" && etapeProjet === 2,      bloque: !projetOuvert },
    { label: "🔍 Analyse",          action: () => lancerAnalyse(donneesProjet), actif: onglet === "solution",                     bloque: !analyseNavActive, special: true },
    { label: t("nav.agent_ia"),     action: () => setOnglet("agent"),       actif: onglet === "agent",                           bloque: !analyseComplete },
    { label: t("nav.comparaison"),  action: () => setOnglet("comparaison"), actif: onglet === "comparaison",                     bloque: !analyseComplete },
    { label: "📄 Rapport",          action: () => setOnglet("rapport"),         actif: onglet === "rapport",                         bloque: !analyseComplete },
    { label: t("nav.copilote"),     action: () => setOnglet("copilote"),    actif: onglet === "copilote",                        bloque: currentUser?.copilote_mode === "complet" ? false : !analyseComplete },
    { label: t("nav.aide"),         action: () => setShowHelp(true),        actif: false,                                        bloque: false },
  ];

  // ── Écrans auth ──────────────────────────────────────
  if (authState === "checking") return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#f0fdf4", fontFamily:"'Segoe UI', system-ui, sans-serif" }}>
      <div style={{ textAlign:"center", color:"#2D7A2D" }}>
        <div style={{ fontSize:40, marginBottom:16 }}>🏗️</div>
        <div style={{ fontSize:15, fontWeight:600 }}>Chargement de GEO5 Expert Advisor...</div>
      </div>
    </div>
  );
  if (authState === "accueil" || authState === "login") return <PagePublique onLogin={handleLogin} onAdmin={handleAdmin} />;
  if (authState === "expire") return <AccesExpire onDeconnexion={handleDeconnexion} />;
  if (authState === "admin") return <AdminPanel onClose={handleDeconnexion} fullPage />;

  // ── Modale droit d'auteur (une fois par session) ─────
  const isEU = currentUser?.licence === "eu";
  if (!copyrightAccepted) return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"linear-gradient(135deg,#0d1f12,#1a3a1a)", fontFamily:"'Segoe UI', system-ui, sans-serif", padding:24 }}>
      <div style={{ background:"#fff", borderRadius:20, maxWidth:640, width:"100%", boxShadow:"0 32px 80px rgba(0,0,0,0.5)", overflow:"hidden" }}>

        {/* Header */}
        <div style={{ background:"linear-gradient(135deg,#2D7A2D,#1B5E20)", padding:"28px 32px", textAlign:"center" }}>
          <div style={{ fontSize:32, marginBottom:8 }}>⚖️</div>
          <div style={{ fontSize:18, fontWeight:900, color:"#fff", letterSpacing:0.5 }}>
            {isEU ? "Terms of Use — Intellectual Property" : "Conditions d'utilisation — Propriété intellectuelle"}
          </div>
          <div style={{ fontSize:12, color:"rgba(255,255,255,0.7)", marginTop:4, letterSpacing:1.5, textTransform:"uppercase" }}>
            GEO5 Expert Advisor · © 2025 Civisoft
          </div>
        </div>

        {/* Corps */}
        <div style={{ padding:"28px 32px" }}>
          <div style={{ fontSize:13, color:"#374151", lineHeight:1.8, borderLeft:"3px solid #2D7A2D", paddingLeft:16 }}>
            {isEU ? (
              <>
                <p style={{ margin:"0 0 10px" }}>This software, its geotechnical content, algorithms and knowledge base are the <strong>exclusive property of Civisoft</strong>, protected by copyright and intellectual property laws.</p>
                <p style={{ margin:"0 0 10px" }}>Any <strong>reproduction, copying, distribution, modification or reverse engineering</strong>, in whole or in part, is strictly prohibited without prior written authorisation.</p>
                <p style={{ margin:"0 0 10px" }}>Access is granted on a <strong>personal, non-transferable basis</strong> under the subscribed licence only.</p>
                <p style={{ margin:"0 0 10px" }}>Results provided by this tool are <strong>decision-support aids</strong> and do not replace the judgement of a qualified geotechnical engineer.</p>
                <p style={{ margin:"0 0 10px" }}>The user assumes <strong>full professional responsibility</strong> for the use of results in their projects.</p>
                <p style={{ margin:0, color:"#DC2626", fontWeight:600 }}>Any violation will be subject to civil and/or criminal proceedings under applicable law.</p>
              </>
            ) : (
              <>
                <p style={{ margin:"0 0 10px" }}>Ce logiciel, son contenu géotechnique, ses algorithmes et sa base de connaissances sont la <strong>propriété exclusive de Civisoft</strong>, protégés par les lois sur le droit d'auteur et la propriété intellectuelle.</p>
                <p style={{ margin:"0 0 10px" }}>Toute <strong>reproduction, copie, distribution, modification ou ingénierie inverse</strong>, partielle ou totale, est strictement interdite sans autorisation écrite préalable.</p>
                <p style={{ margin:"0 0 10px" }}>L'accès est accordé à titre <strong>personnel et non transférable</strong>, dans le cadre de la licence souscrite uniquement.</p>
                <p style={{ margin:"0 0 10px" }}>Les résultats fournis constituent une <strong>aide à la décision</strong> et ne remplacent pas le jugement d'un ingénieur géotechnicien qualifié.</p>
                <p style={{ margin:"0 0 10px" }}>L'utilisateur assume l'<strong>entière responsabilité professionnelle</strong> de l'utilisation des résultats dans ses projets.</p>
                <p style={{ margin:0, color:"#DC2626", fontWeight:600 }}>Toute violation fera l'objet de poursuites civiles et/ou pénales conformément à la législation applicable.</p>
              </>
            )}
          </div>

          {/* Checkbox */}
          <label style={{ display:"flex", alignItems:"center", gap:12, marginTop:24, cursor:"pointer", userSelect:"none" }}>
            <input
              type="checkbox"
              checked={copyrightChecked}
              onChange={e => setCopyrightChecked(e.target.checked)}
              style={{ width:20, height:20, accentColor:"#2D7A2D", cursor:"pointer", flexShrink:0 }}
            />
            <span style={{ fontSize:14, fontWeight:700, color:"#1a1a2e" }}>
              {isEU ? "I have read and agree to the above terms." : "Lu et approuvé — J'accepte les conditions ci-dessus."}
            </span>
          </label>

          {/* Bouton */}
          <button
            onClick={() => {
              if (!copyrightChecked) return;
              const key = `geo5_copyright_${currentUser?.id || currentUser?.email}`;
              localStorage.setItem(key, "1");
              setCopyrightAccepted(true);
              // Notification silencieuse au backend
              const token = localStorage.getItem("geo5_token");
              if (token) {
                fetch(`${API}/auth/copyright-accepted`, {
                  method: "POST",
                  headers: { Authorization: `Bearer ${token}` },
                }).catch(() => {}); // Silencieux
              }
            }}
            disabled={!copyrightChecked}
            style={{
              marginTop:20, width:"100%", padding:"14px 0", fontSize:15, fontWeight:800,
              borderRadius:10, border:"none", cursor: copyrightChecked ? "pointer" : "not-allowed",
              background: copyrightChecked ? "linear-gradient(135deg,#2D7A2D,#1B5E20)" : "#d1d5db",
              color: copyrightChecked ? "#fff" : "#9ca3af",
              transition:"all 0.2s", letterSpacing:0.5,
            }}
          >
            {isEU ? "▶ Access GEO5 Expert Advisor" : "▶ Accéder à GEO5 Expert Advisor"}
          </button>
        </div>
      </div>
    </div>
  );

  // ── Rendu principal ──────────────────────────────────
  const showGlobalHeader = !["presentation", "demarrage"].includes(onglet);

  return (
    <div style={{ minHeight:"100vh", background: onglet==="copilote" ? "linear-gradient(160deg,#0d1f12,#132b1a,#0a1e10)" : onglet==="agent" ? "#0f172a" : "#fff", fontFamily:"'Segoe UI', system-ui, sans-serif", color:"#1a1a2e" }}>

      {showHelp       && <HelpModal   onClose={() => setShowHelp(false)} onglet={onglet} etapeProjet={etapeProjet} />}
      {showAdminPanel && <AdminPanel  onClose={() => setShowAdminPanel(false)} />}
      {showPopupProfil && (
        <PopupProfil currentUser={currentUser} onTerminer={(profil) => { setProfilUtilisateur(profil); setShowPopupProfil(false); }} />
      )}

      {/* ══ MODAL VÉRIFICATION KB VERIF-01→06 ══ */}
      {verifModal && (
        <div style={{ position:"fixed", inset:0, zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(0,0,0,0.55)" }}>
          <div style={{ background:"#fff", borderRadius:12, padding:32, boxShadow:"0 8px 32px rgba(0,0,0,0.2)", maxWidth:520, width:"90%", fontFamily:"'Segoe UI', system-ui, sans-serif" }}>

            {verifModal.erreurs.length > 0 ? (
              <>
                <div style={{ fontSize:28, textAlign:"center", marginBottom:8 }}>⛔</div>
                <div style={{ fontSize:17, fontWeight:800, color:"#b91c1c", textAlign:"center", marginBottom:6 }}>Données incomplètes</div>
                <div style={{ fontSize:13, color:"#6B7280", textAlign:"center", marginBottom:20 }}>Corrigez les points suivants avant de lancer l'analyse.</div>
                <ul style={{ margin:"0 0 24px", paddingLeft:20, display:"flex", flexDirection:"column", gap:8 }}>
                  {verifModal.erreurs.map(e => (
                    <li key={e.id} style={{ fontSize:13, color:"#7f1d1d" }}>
                      <strong style={{ fontFamily:"monospace", marginRight:6, color:"#b91c1c" }}>[{e.id}]</strong>
                      <strong>{e.champ}</strong> — {e.message}
                    </li>
                  ))}
                </ul>
                <button onClick={() => setVerifModal(null)}
                  style={{ width:"100%", padding:"12px", background:"#b91c1c", border:"none", borderRadius:8, fontSize:14, fontWeight:700, color:"#fff", cursor:"pointer" }}>
                  ← Corriger les données
                </button>
              </>
            ) : (
              <>
                <div style={{ fontSize:28, textAlign:"center", marginBottom:8 }}>⚠️</div>
                <div style={{ fontSize:17, fontWeight:800, color:"#d97706", textAlign:"center", marginBottom:6 }}>Vérification cohérence des données projet</div>
                <div style={{ fontSize:13, color:"#6B7280", textAlign:"center", marginBottom:20 }}>Des incohérences ont été détectées. Vous pouvez continuer ou revoir les données.</div>
                <ul style={{ margin:"0 0 24px", paddingLeft:20, display:"flex", flexDirection:"column", gap:8 }}>
                  {verifModal.avertissements.map(a => (
                    <li key={a.id} style={{ fontSize:13, color:"#78350f" }}>
                      <strong style={{ fontFamily:"monospace", marginRight:6, color:"#d97706" }}>[{a.id}]</strong>
                      <strong>{a.champ}</strong> — {a.message}
                    </li>
                  ))}
                </ul>
                <div style={{ display:"flex", gap:10 }}>
                  <button onClick={() => setVerifModal(null)}
                    style={{ flex:1, padding:"11px", background:"transparent", border:"1.5px solid #E5E7EB", borderRadius:8, fontSize:13, fontWeight:600, color:"#374151", cursor:"pointer" }}>
                    ← Revoir les données
                  </button>
                  <button onClick={() => { setDonneesProjet(verifModal.pendingData); setDiagnosticBackend(null); setOnglet("solution"); setVerifModal(null); }}
                    style={{ flex:1, padding:"11px", background:"#2D7A2D", border:"none", borderRadius:8, fontSize:13, fontWeight:700, color:"#fff", cursor:"pointer" }}>
                    Continuer l'analyse →
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ══ MODALE CONFIRMATION DÉCONNEXION ══ */}
      {showDeconnexionConfirm && (
        <div style={{ position:"fixed", inset:0, zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(0,0,0,0.6)" }}>
          <div style={{ background:"#fff", borderRadius:16, padding:32, maxWidth:420, width:"90%", boxShadow:"0 24px 80px rgba(0,0,0,0.4)", fontFamily:"'Segoe UI', system-ui, sans-serif" }}>
            <div style={{ fontSize:36, textAlign:"center", marginBottom:12 }}>⚠️</div>
            <div style={{ fontSize:18, fontWeight:800, color:"#1a1a2e", textAlign:"center", marginBottom:8 }}>{t("deconnexion.titre")}</div>
            <div style={{ fontSize:14, color:"#6B7280", textAlign:"center", marginBottom:24, lineHeight:1.6 }}>
              {t("deconnexion.message")}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              <button onClick={sauvegarderEtQuitter}
                style={{ width:"100%", padding:"13px", background:"linear-gradient(135deg,#2D7A2D,#00A651)", border:"none", borderRadius:10, fontSize:14, fontWeight:800, color:"#fff", cursor:"pointer" }}>
                {t("deconnexion.sauvegarder")}
              </button>
              <div style={{ display:"flex", gap:10 }}>
                <button onClick={() => setShowDeconnexionConfirm(false)}
                  style={{ flex:1, padding:"11px", background:"transparent", border:"1.5px solid #E5E7EB", borderRadius:10, fontSize:13, fontWeight:600, color:"#374151", cursor:"pointer" }}>
                  {t("deconnexion.annuler")}
                </button>
                <button onClick={handleDeconnexion}
                  style={{ flex:1, padding:"11px", background:"linear-gradient(135deg,#DC2626,#B91C1C)", border:"none", borderRadius:10, fontSize:13, fontWeight:700, color:"#fff", cursor:"pointer" }}>
                  {t("deconnexion.quitter_sans")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ BOUTON DÉCONNEXION FLOTTANT (page demarrage seulement) ══ */}
      {onglet === "demarrage" && (
        <button onClick={demanderDeconnexion}
          style={{ position:"fixed", top:16, right:16, zIndex:400, background:"rgba(255,255,255,0.15)", border:"1.5px solid rgba(255,255,255,0.3)", borderRadius:8, padding:"8px 16px", fontSize:12, fontWeight:700, color:"#fff", cursor:"pointer", backdropFilter:"blur(8px)" }}>
          {t("nav.deconnexion")}
        </button>
      )}

      {/* ══ FORMULAIRE NOUVEAU PROJET ══ */}
      {showNouveauProjet && (
        <FormulaireClient
          onDemarrer={handleNouveauProjet}
          onClose={() => setShowNouveauProjet(false)}
          pays="france"
          userId={currentUser?.id}
        />
      )}

      {/* ══ PANEL MES PROJETS ══ */}
      {showProjets && (
        <div style={{ position:"fixed", inset:0, zIndex:500, display:"flex" }}>
          {/* Overlay */}
          <div onClick={() => setShowProjets(false)} style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.5)" }}/>
          {/* Panel latéral */}
          <div style={{
            position:"relative", marginLeft:"auto", width:420, background:"#fff",
            height:"100vh", overflowY:"auto", boxShadow:"-4px 0 24px rgba(0,0,0,0.2)",
            display:"flex", flexDirection:"column"
          }}>
            <div style={{ background:"#2D7A2D", padding:"20px 24px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div style={{ color:"#fff", fontSize:18, fontWeight:800 }}>📂 Mes Projets</div>
              <button onClick={() => setShowProjets(false)} style={{ background:"transparent", border:"none", color:"#fff", fontSize:20, cursor:"pointer" }}>✕</button>
            </div>
            <div style={{ padding:20, flex:1 }}>
              {projetsListe.length === 0 ? (
                <div style={{ textAlign:"center", color:"#9ca3af", padding:"40px 20px" }}>
                  <div style={{ fontSize:40, marginBottom:12 }}>📋</div>
                  <div style={{ fontSize:14 }}>Aucun projet sauvegardé.</div>
                  <div style={{ fontSize:12, marginTop:6 }}>Utilisez le bouton "💾 Sauvegarder" dans la page Solution.</div>
                </div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {[...projetsListe].reverse().map(p => (
                    <div key={p.id} style={{
                      border:"1.5px solid #E0E0E0", borderRadius:10, padding:"14px 16px",
                      background:"#fafafa", display:"flex", alignItems:"center", gap:12
                    }}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:700, fontSize:13, color:"#1a1a1a", marginBottom:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {p.donneesProjet?.clientInfo?.nomEntreprise || p.nom}
                        </div>
                        {p.donneesProjet?.clientInfo?.codeRef && (
                          <div style={{ fontSize:11, color:"#6A1B9A", fontFamily:"monospace", fontWeight:700, marginBottom:2 }}>
                            {p.donneesProjet.clientInfo.codeRef}
                          </div>
                        )}
                        <div style={{ fontSize:11, color:"#9ca3af" }}>
                          {new Date(p.date).toLocaleDateString("fr-FR", { day:"2-digit", month:"short", year:"numeric" })}
                          {p.donneesProjet?.clientInfo?.wilaya && ` · ${p.donneesProjet.clientInfo.wilaya.nom}`}
                          {p.donneesProjet?.hauteur && ` · H=${p.donneesProjet.hauteur}m`}
                        </div>
                      </div>
                      <button onClick={() => chargerProjet(p)} style={{
                        background:"#2D7A2D", border:"none", borderRadius:7,
                        color:"#fff", padding:"7px 14px", fontSize:12, fontWeight:700, cursor:"pointer"
                      }}>Charger</button>
                      <button onClick={() => supprimerProjet(p.id)} style={{
                        background:"transparent", border:"1px solid #e5e7eb", borderRadius:7,
                        color:"#9ca3af", padding:"7px 10px", fontSize:12, cursor:"pointer"
                      }}>🗑</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══ HEADER GLOBAL (toutes pages sauf Présentation) ══ */}
      {showGlobalHeader && (
        <header style={{ position:"sticky", top:0, zIndex:600, fontFamily:"'Segoe UI', system-ui, sans-serif" }}>

          {/* Ligne 1 — blanche : logos + titre */}
          <div style={{ background:"#fff", borderBottom:"1px solid #E0E0E0", padding:"10px 32px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            {/* Logo Civisoft */}
            <div style={{ width:180, display:"flex", alignItems:"center" }}>
              {LOGO_CIVISOFT
                ? <img src={LOGO_CIVISOFT} alt="CiviSoft" style={{ height:48, objectFit:"contain" }}/>
                : <div style={{ border:"1px solid #ccc", borderRadius:6, padding:"6px 14px", fontSize:12, color:"#666" }}>Logo Civisoft</div>
              }
            </div>

            {/* Logo GeoExpert central */}
            <div style={{ textAlign:"center", flex:1 }}>
              <img src="/assets/logo_geoexpert.png" alt="GeoExpert" style={{ height:48, objectFit:"contain" }}/>
              <div style={{ fontSize:12, color:"#666", marginTop:2 }}>
                V1.0 2026
                {currentUser?.expire_info && (
                  <span style={{ marginLeft:12, color: currentUser.expire_info.jours_restants <= 5 ? "#f59e0b" : "#2D7A2D", fontWeight:700 }}>
                    · {currentUser.expire_info.jours_restants}j restants
                  </span>
                )}
              </div>
            </div>

            {/* Logo GEO5 Partenaire */}
            <div style={{ width:180, display:"flex", alignItems:"center", justifyContent:"flex-end" }}>
              {LOGO_GEO5
                ? <img src={LOGO_GEO5} alt="GEO5 Authorized Partner" style={{ height:44, objectFit:"contain" }}/>
                : <div style={{ border:"1px solid #ccc", borderRadius:6, padding:"6px 14px", fontSize:12, color:"#666" }}>Logo Partenaire</div>
              }
            </div>
          </div>

          {/* Ligne 2 — verte : navigation complète */}
          <div style={{ background:"#2D7A2D", padding:"0 32px", display:"flex", alignItems:"stretch" }}>
            {NAV_ITEMS.map((item, i) => (
              <button key={i}
                onClick={item.bloque ? undefined : item.action}
                title={item.bloque ? (!projetOuvert && i > 0 && i < 5 ? "Créez un projet d'abord" : item.special ? "Complétez les 3 étapes pour analyser" : "Lancez l'analyse du projet (étape 3 → Analyser)") : ""}
                style={item.special ? {
                  background: item.bloque ? "transparent" : item.actif ? "#1B5E20" : "#2D7A2D",
                  border: "1.5px solid " + (item.bloque ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.6)"),
                  borderRadius: 6,
                  color: item.bloque ? "rgba(255,255,255,0.35)" : "#fff",
                  padding: "6px 16px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: item.bloque ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                  margin: "auto 8px",
                  transition: "all 0.15s",
                  opacity: item.bloque ? 0.5 : 1,
                } : {
                  background: item.actif ? "rgba(0,0,0,0.25)" : "transparent",
                  border: "none",
                  borderBottom: item.actif ? "3px solid #fff" : "3px solid transparent",
                  color: item.bloque ? "rgba(255,255,255,0.30)" : item.actif ? "#fff" : "rgba(255,255,255,0.85)",
                  padding: "12px 18px",
                  fontSize: 13,
                  fontWeight: item.actif ? 700 : 500,
                  cursor: item.bloque ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                  transition: "all 0.15s",
                  opacity: item.bloque ? 0.5 : 1,
                }}
                onMouseEnter={e => { if (!item.actif && !item.bloque && !item.special) { e.currentTarget.style.background="rgba(255,255,255,0.1)"; e.currentTarget.style.color="#fff"; }}}
                onMouseLeave={e => { if (!item.actif && !item.bloque && !item.special) { e.currentTarget.style.background="transparent"; e.currentTarget.style.color="rgba(255,255,255,0.85)"; }}}
              >
                {item.bloque ? "🔒 " : ""}{item.label}
              </button>
            ))}

            {/* Spacer */}
            <div style={{ flex:1 }}/>



            {/* Déconnexion */}
            <button onClick={demanderDeconnexion} style={{ background:"transparent", border:"none", borderBottom:"3px solid transparent", color:"#fff", padding:"12px 18px", fontSize:13, fontWeight:800, cursor:"pointer", whiteSpace:"nowrap" }}>
              {t("nav.deconnexion")}
            </button>
          </div>
        </header>
      )}

      {/* ══ CONTENU PRINCIPAL ══ */}
      <div style={{ maxWidth: ["presentation","projet","solution"].includes(onglet) ? "100%" : 1280, margin:"0 auto", padding: ["presentation","projet","solution"].includes(onglet) ? 0 : "32px 24px", boxSizing:"border-box", overflowX:"hidden" }}>

        {onglet === "presentation" && <PageAccueil onEnter={(o) => setOnglet(o === "projet" ? "demarrage" : o)} onLogout={demanderDeconnexion} licence={currentUser?.licence || "fr"} />}

        {onglet === "demarrage" && (
          <div style={{ minHeight:"100vh", background:"linear-gradient(160deg,#f0fdf4,#e8f5e9)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", fontFamily:"'Segoe UI', system-ui, sans-serif", padding:"40px 24px" }}>
            {/* Logo GeoExpert */}
            <div style={{ marginBottom:32 }}>
              <img src="/assets/logo_geoexpert.png" alt="GeoExpert" style={{ height:64, objectFit:"contain" }}/>
            </div>

            <div style={{ textAlign:"center", marginBottom:16 }}>
              <div style={{ fontSize:28, fontWeight:900, color:"#1A1A1A", marginBottom:8 }}>GeoExpert</div>
              <div style={{ fontSize:15, color:"#4B5563" }}>Vos projets géotechniques</div>
            </div>

            {/* 2 tuiles */}
            <div style={{ display:"flex", gap:24, marginTop:40, flexWrap:"wrap", justifyContent:"center" }}>

              {/* Nouveau Projet */}
              <button
                onClick={() => setShowNouveauProjet(true)}
                style={{
                  width:280, padding:"36px 28px", background:"#fff",
                  border:"2px solid #2D7A2D", borderRadius:16, cursor:"pointer",
                  textAlign:"center", boxShadow:"0 4px 20px rgba(45,122,45,0.15)",
                  transition:"all 0.2s"
                }}
                onMouseEnter={e => { e.currentTarget.style.background="#2D7A2D"; e.currentTarget.style.color="#fff"; }}
                onMouseLeave={e => { e.currentTarget.style.background="#fff"; e.currentTarget.style.color="#1A1A1A"; }}
              >
                <div style={{ fontSize:44, marginBottom:14 }}>➕</div>
                <div style={{ fontSize:17, fontWeight:800, marginBottom:8, color:"inherit" }}>Nouveau Projet</div>
                <div style={{ fontSize:13, color:"#6B7280", lineHeight:1.5 }}>Créer un nouveau dossier géotechnique avec identification du client</div>
              </button>

              {/* Consulter mes Projets */}
              <button
                onClick={() => { setProjetsListe(getProjets()); setShowProjets(true); }}
                style={{
                  width:280, padding:"36px 28px", background:"#fff",
                  border:"2px solid #1565C0", borderRadius:16, cursor:"pointer",
                  textAlign:"center", boxShadow:"0 4px 20px rgba(21,101,192,0.12)",
                  transition:"all 0.2s"
                }}
                onMouseEnter={e => { e.currentTarget.style.background="#1565C0"; e.currentTarget.style.color="#fff"; }}
                onMouseLeave={e => { e.currentTarget.style.background="#fff"; e.currentTarget.style.color="#1A1A1A"; }}
              >
                <div style={{ fontSize:44, marginBottom:14 }}>📋</div>
                <div style={{ fontSize:17, fontWeight:800, marginBottom:8, color:"inherit" }}>Consulter mes Projets</div>
                <div style={{ fontSize:13, color:"#6B7280", lineHeight:1.5 }}>
                  {projetsListe.length > 0
                    ? <><strong style={{ color:"#1565C0" }}>{projetsListe.length} projet{projetsListe.length > 1 ? "s" : ""}</strong> sauvegardé{projetsListe.length > 1 ? "s" : ""}</>
                    : "Aucun projet sauvegardé pour l'instant"}
                </div>
              </button>
            </div>

            {/* Lien retour présentation */}
            <button onClick={() => setOnglet("presentation")} style={{ marginTop:40, background:"transparent", border:"none", color:"#9CA3AF", fontSize:13, cursor:"pointer" }}>
              ← Retour Accueil
            </button>
          </div>
        )}

        {onglet === "projet" && (
          <PageProjet
            key={donneesProjet?._projetId || donneesProjet?.clientInfo?.codeRef || "default"}
            onEnter={(o) => setOnglet(o)}
            setDonneesProjet={setDonneesProjet}
            etapeInitiale={etapeProjet}
            onEtapeChange={setEtapeProjet}
            donneesInitiales={donneesProjet}
            pays="france"
            onAnalyser={lancerAnalyse}
          />
        )}

        {onglet === "solution" && (
          <PageSolution
            donneesProjet={donneesProjet}
            profilUtilisateur={profilUtilisateur}
            onRetour={() => setOnglet("presentation")}
            onAgent={() => setOnglet("agent")}
            onRapport={() => setOnglet("rapport")}
            onSolutions={(sols, alertes, modeSec) => setDonneesProjet(prev => ({ ...prev, solutions: sols, alertes_projet: alertes || [], mode_securite: modeSec || false }))}
            onModifier={(etape) => { setProjetModifie(true); allerProjet(etape); }}
            projetModifie={projetModifie}
            onClientInfoUpdate={(newCI) => {
              setDonneesProjet(prev => ({ ...prev, clientInfo: newCI }));
              setProjetModifie(false);
            }}
            userId={currentUser?.id}
            onDiagnosticBackend={setDiagnosticBackend}
            modeConsultationProjet={modeConsultationProjet}
            onProjetClone={onProjetClone}
            onSolutionRetenue={(retenue) => setDonneesProjet(prev => ({ ...prev, solution_retenue: retenue }))}
          />
        )}

        {onglet === "rapport" && (
          <PageRapport
            diagnosticBackend={diagnosticBackend}
            donneesProjet={donneesProjet}
            onRetour={() => setOnglet("solution")}
            onAgent={() => setOnglet("agent")}
            onSupprDialogueRapport={(index) => setDonneesProjet(prev => {
              if (!prev) return prev;
              const actuel = prev.dialogueExpertRapport || [];
              return { ...prev, dialogueExpertRapport: actuel.filter((_, i) => i !== index) };
            })}
          />
        )}

        {onglet === "copilote" && (
          <TabCoPilote
            donneesProjet={donneesProjet}
            hasCopilote={currentUser?.has_copilote}
            copiloteMode={currentUser?.copilote_mode || "solution"}
            pays="france"
            kbSource={currentUser?.copilote_kb_source || "frontend"}
            userId={currentUser?.id}
            onRetenirSolution={(retenue) => setDonneesProjet(prev => ({ ...prev, solution_retenue: retenue }))}
            diagnosticBackend={diagnosticBackend}
          />
        )}

        {onglet === "agent" && (
          <TabAgentIA
            donneesProjet={donneesProjet}
            profilUtilisateur={profilUtilisateur}
            isAdmin={currentUser?.is_admin}
            licence={currentUser?.licence}
            diagnosticBackend={diagnosticBackend}
            solutionId={diagnosticBackend?.adequation?.classement?.[0]?.id || null}
            solutions={diagnosticBackend?.adequation?.classement || []}
            onAjoutDialogueRapport={(entry) => setDonneesProjet(prev => {
              if (!prev) return prev;
              const actuel = prev.dialogueExpertRapport || [];
              if (actuel.length >= 3) return prev;
              if (actuel.some(e => e.solutionId === entry.solutionId && e.question === entry.question)) return prev;
              return { ...prev, dialogueExpertRapport: [...actuel, entry] };
            })}
          />
        )}

        {onglet === "comparaison" && (
          <div>
            <div style={{ marginBottom:28 }}>
              <h2 style={{ color:"#1A1A1A", fontWeight:800, fontSize:24, margin:"0 0 6px" }}>⚖️ Comparaison Budget & Fiabilité d'exécution</h2>
              <p style={{ color:"#64748b", fontSize:14, margin:0 }}>Comparaison budgétaire des solutions — prix indicatifs.</p>
            </div>
            <TabComparaison
              donneesProjet={donneesProjet}
              pays={currentUser?.licence === "eu" ? "eu" : "france"}
              diagnosticBackend={diagnosticBackend}
              onUpdateComparaisonPrix={(prix) => setDonneesProjet(prev => prev ? { ...prev, comparaisonPrix: prix } : prev)}
              onUpdateComparaisonParams={(p) => setDonneesProjet(prev => prev ? { ...prev, comparaisonParams: p } : prev)}
            />
          </div>
        )}

      </div>
    </div>
  );
}
